'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { XCircle } from 'phosphor-icons';
import { useCallback, useMemo, useState } from 'react';
import { c } from 'ui-components';
import { deselectOwnershipFilter } from '../utils/clearOwnershipFilter';
import {
  resolveDateRangeLabel,
  resolveNumericRangeLabel,
  resolveSaleLabel,
} from '../utils/facetLabels';
import { FacetBreadcrumbsControlProps } from './FacetBreadcrumbs.types';

const MAX_VISIBLE_BREADCRUMBS = 5;

// Chip prefix per breadcrumb field, resolved from the same microcopy keys as
// the facet titles so the chips localize with the facet lists above them.
// ec_category never produces a chip (filtered below).
const FIELD_LABEL_KEYS: Record<string, { key: string; fallback: string }> = {
  ec_price_filter: { key: 'search.filter-price-label', fallback: 'Price' },
  ec_rating: { key: 'search.filter-rating-label', fallback: 'Rating' },
  ec_sale_filters: { key: 'search.filter-onsale-label', fallback: 'Sale' },
  publisher_name: {
    key: 'search.filter-publisher-label',
    fallback: 'Publisher',
  },
  first_published_at: {
    key: 'search.filter-release-date-label',
    fallback: 'Release Date',
  },
};

/**
 * Presentational breadcrumb chips — the caller supplies the breadcrumb-manager
 * state and deselect-all.
 */
export const FacetBreadcrumbsControl = ({
  state,
  onDeselectAll,
  className,
  isReady = true,
}: FacetBreadcrumbsControlProps) => {
  const session = useSession();
  const searchParams = useSearchParams();
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const valueBundles = useMemo(
    () => microcopySetToResourceBundles(searchValuesMicrocopy ?? []),
    [searchValuesMicrocopy]
  );

  const clearLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.breadcrumbs.clear',
    'Clear'
  );
  const showMoreLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.breadcrumbs.show-more',
    'Show more'
  );
  const showLessLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.breadcrumbs.show-less',
    'Show less'
  );
  const ownershipTitle = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.ownership',
    'Ownership'
  );
  const ownershipLabel = getMicrocopyValue(
    searchValuesMicrocopy,
    'search-ui-values.hide-owned-assets',
    'Hide owned assets'
  );

  const [showAll, setShowAll] = useState(false);

  // Ownership is only offered to signed-in users — OwnershipFacet renders
  // nothing otherwise — so a `hideOwnership` deep link must not chip
  // for an anonymous visitor, who has no facet toggle to reconcile it against.
  const isOwnershipActive =
    session.status === 'authenticated' &&
    searchParams?.get('hideOwnership') === 'true';

  const deselectOwnership = useCallback(() => deselectOwnershipFilter(), []);

  const allBreadcrumbs = useMemo(() => {
    const items: {
      field: string;
      label: string;
      deselect: () => void;
      disabled: boolean;
    }[] = [];

    const formatLabel = (field: string, value: string) => {
      const fieldLabel = FIELD_LABEL_KEYS[field];
      return fieldLabel
        ? `${getMicrocopyValue(searchLabelsMicrocopy, fieldLabel.key, fieldLabel.fallback)}: ${value}`
        : value;
    };

    const getSaleLabel = (value: string) =>
      resolveSaleLabel(valueBundles.ec_sale_filters, value);

    state.facetBreadcrumbs
      ?.filter((facet) => facet.field !== 'ec_category')
      .forEach((facet) =>
        facet.values.forEach((breadcrumb) => {
          const displayValue =
            facet.field === 'ec_sale_filters'
              ? getSaleLabel(breadcrumb.value.value)
              : breadcrumb.value.value;
          items.push({
            field: facet.field,
            label: formatLabel(facet.field, displayValue),
            deselect: breadcrumb.deselect,
            disabled: !isReady,
          });
        })
      );

    state.numericFacetBreadcrumbs?.forEach((facet) =>
      facet.values.forEach((breadcrumb) => {
        const rangeLabel = resolveNumericRangeLabel(
          facet.field,
          breadcrumb.value.start,
          breadcrumb.value.end,
          valueBundles
        );
        items.push({
          field: facet.field,
          label: formatLabel(facet.field, rangeLabel),
          deselect: breadcrumb.deselect,
          disabled: !isReady,
        });
      })
    );

    state.dateFacetBreadcrumbs?.forEach((facet) =>
      facet.values.forEach((breadcrumb) => {
        const rangeLabel = resolveDateRangeLabel(
          breadcrumb.value.start,
          breadcrumb.value.end,
          valueBundles.first_published_at
        );
        items.push({
          field: facet.field,
          label: formatLabel(facet.field, rangeLabel),
          deselect: breadcrumb.deselect,
          disabled: !isReady,
        });
      })
    );

    // Ownership (custom filter, not a Coveo facet): URL- and DOM-based, so it
    // stays actionable without the engine — like the ownership toggle itself.
    if (isOwnershipActive) {
      items.push({
        field: 'ownership',
        label: `${ownershipTitle}: ${ownershipLabel}`,
        deselect: deselectOwnership,
        disabled: false,
      });
    }

    return items;
  }, [
    state.facetBreadcrumbs,
    state.numericFacetBreadcrumbs,
    state.dateFacetBreadcrumbs,
    isOwnershipActive,
    ownershipTitle,
    ownershipLabel,
    valueBundles,
    searchLabelsMicrocopy,
    deselectOwnership,
    isReady,
  ]);

  const handleClearAll = useCallback(() => {
    onDeselectAll();
    if (isOwnershipActive) {
      deselectOwnership();
    }
  }, [onDeselectAll, isOwnershipActive, deselectOwnership]);

  if (allBreadcrumbs.length === 0) {
    return null;
  }

  const visibleBreadcrumbs = showAll
    ? allBreadcrumbs
    : allBreadcrumbs.slice(0, MAX_VISIBLE_BREADCRUMBS);
  const hasMore = allBreadcrumbs.length > MAX_VISIBLE_BREADCRUMBS;

  // Clear removes every chip, so it is actionable exactly when they all are:
  // pre-hydration that means an ownership-only chip set, whose deselect needs
  // no engine.
  const isClearDisabled = allBreadcrumbs.some((item) => item.disabled);

  return (
    <div
      className={c('flex flex-wrap items-center gap-2', className)}
      data-test="facet-breadcrumbs"
    >
      {visibleBreadcrumbs.map((breadcrumb, index) => (
        <button
          key={`${breadcrumb.field}-${breadcrumb.label}-${index}`}
          type="button"
          onClick={breadcrumb.deselect}
          disabled={breadcrumb.disabled}
          aria-label={`Remove ${breadcrumb.label} filter`}
          className="flex min-h-11 items-center gap-1 rounded-full bg-grey-100 py-1 pl-3 pr-2 text-sm font-medium leading-5 text-grey-700 disabled:cursor-not-allowed disabled:text-grey-400 lg:min-h-0"
        >
          {breadcrumb.label}
          <XCircle
            size={16}
            className={c(
              'shrink-0',
              breadcrumb.disabled ? 'text-grey-400' : 'text-grey-500'
            )}
          />
        </button>
      ))}

      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="inline-flex min-h-11 min-w-11 items-center justify-center px-1.5 py-1.5 text-[13px] font-medium leading-5 text-grey-500 lg:min-h-0 lg:min-w-0"
        >
          {showAll ? showLessLabel : showMoreLabel}
        </button>
      )}

      <button
        type="button"
        onClick={handleClearAll}
        disabled={isClearDisabled}
        className="inline-flex min-h-11 min-w-11 items-center justify-center px-1.5 py-1.5 text-[13px] font-medium leading-5 text-grey-500 disabled:cursor-not-allowed disabled:text-grey-400 lg:min-h-0 lg:min-w-0"
      >
        {clearLabel}
      </button>
    </div>
  );
};
